// Hotel filter schema (MMT-style 15 categories). The result objects from /api/v2/hotels/search
// don't always carry every flag, so we derive tags heuristically from rating/price/name/amenities.
// All filters provide:  options(results) → list of {id,label,count}  and  apply(hotel,selected) → bool.

const uniq = (xs) => Array.from(new Set(xs));
const lower = (s) => String(s || '').toLowerCase();

// ─── Heuristic tag derivation ─────────────────────────────────────────────
const KNOWN_CHAINS = [
  'Taj', 'Marriott', 'Hyatt', 'Hilton', 'Lemon Tree', 'ITC', 'Accor', 'Radisson',
  'Sheraton', 'Westin', 'Novotel', 'OYO', 'FabHotel', 'Treebo', 'Ginger', 'Holiday Inn',
  'Crowne Plaza', 'IHG', 'Four Seasons', 'Oberoi', 'Leela', 'Park Hyatt',
];

const PROPERTY_TYPES = ['Hotel', 'Villas & Homestays', 'Resort', 'Hostel'];

const detectChain = (name) => {
  const n = lower(name);
  return KNOWN_CHAINS.find((c) => n.includes(lower(c))) || null;
};

const detectPropertyType = (h) => {
  const n = lower(h.name);
  const cat = lower(h.category_name || h.property_type || '');
  if (n.includes('hostel') || cat.includes('hostel')) return 'Hostel';
  if (n.includes('villa') || n.includes('homestay') || cat.includes('villa')) return 'Villas & Homestays';
  if (n.includes('resort') || cat.includes('resort')) return 'Resort';
  return 'Hotel';
};

const detectLocality = (h) => {
  // Use destination_name / district / area / city — first available granular field.
  return h.area || h.district || h.destination_name || h.city || null;
};

const detectAmenities = (h) => {
  // Result objects expose amenities[] but may be inconsistent. Augment from name keywords.
  const base = Array.isArray(h.amenities) ? [...h.amenities] : [];
  const n = lower(h.name);
  const set = new Set(base.map((a) => String(a).trim()));
  if (n.includes('pool') || n.includes('resort')) set.add('Pool');
  if (n.includes('spa')) set.add('Spa');
  if (n.includes('gym') || (h.rating || 0) >= 4) set.add('Gym');
  if (h.rating >= 3) set.add('WiFi');
  if (h.rating >= 3.5) set.add('Parking');
  if (h.rating >= 4) set.add('Restaurant');
  if (n.includes('inn') || h.rating >= 4) set.add('Breakfast Included');
  return Array.from(set);
};

const hotelTags = (h) => {
  // Cached on the object so we don't recompute per filter call.
  if (h.__tags) return h.__tags;
  const price = Number(h.display_price) || 0;
  const rating = Number(h.rating) || 0;
  const amenities = detectAmenities(h);
  const tags = {
    chain: detectChain(h.name),
    property_type: detectPropertyType(h),
    locality: detectLocality(h),
    amenities,
    luxe: rating >= 4.5 && price >= 5000,
    early_bird: price > 0 && price < 4000,
    couple_recommended: rating >= 4.0 && (lower(h.name).includes('suite') || lower(h.name).includes('palace') || lower(h.name).includes('grand') || amenities.includes('Spa')),
    garden_view: lower(h.name).includes('garden') || amenities.includes('Garden View'),
    city_view: lower(h.name).includes('city') || (h.rating || 0) >= 4,
    instant_book: true, // default true for OTA inventory
    entire_property: lower(h.name).includes('villa') || lower(h.name).includes('homestay'),
    caretaker: lower(h.name).includes('homestay'),
    pets_allowed: lower(h.name).includes('pet'),
    self_checkin: rating >= 4.0,
    smoking_allowed: false,
    unmarried_allowed: rating >= 3.5,
    alcohol_allowed: rating >= 4.0,
    cashback: price > 5000,
    coupons: true,
    seasonal_offers: price < 6000,
    flash_sales: rating >= 4 && price < 7000,
    early_checkin: rating >= 4,
    late_checkout: rating >= 4,
  };
  try { Object.defineProperty(h, '__tags', { value: tags, enumerable: false }); } catch { /* readonly object */ }
  return tags;
};

// ─── Price range buckets (₹) ──────────────────────────────────────────────
export const PRICE_BUCKETS = [
  { id: 'b1', label: '₹0 – ₹2,500',       min: 0,      max: 2500 },
  { id: 'b2', label: '₹2,500 – ₹4,500',    min: 2500,   max: 4500 },
  { id: 'b3', label: '₹4,500 – ₹7,500',    min: 4500,   max: 7500 },
  { id: 'b4', label: '₹7,500 – ₹10,500',   min: 7500,   max: 10500 },
  { id: 'b5', label: '₹10,500 – ₹13,000',  min: 10500,  max: 13000 },
  { id: 'b6', label: '₹13,000 – ₹15,000',  min: 13000,  max: 15000 },
  { id: 'b7', label: '₹15,000 – ₹30,000',  min: 15000,  max: 30000 },
  { id: 'b8', label: '₹30,000+',           min: 30000,  max: Infinity },
];

// Filter `apply` functions are guarded: if `sel` is undefined/null we treat as "no selection" → pass.
const noSel = (sel) => sel == null || (Array.isArray(sel) && sel.length === 0);

// ─── Filter group definitions ────────────────────────────────────────────
// type values: 'check-list' (multi-select chip list w/ counts), 'segmented' (single-pick toggle),
//              'price-buckets' (multi-select range buckets), 'custom-range', 'amenity-search',
//              'show-more-list' (with show-more support), 'toggle' (single switch)
export const HOTEL_FILTERS = [
  {
    name: 'suggested', label: 'Suggested For You', type: 'check-list',
    options: (rs) => [
      { id: 'early_bird',         label: 'Early Bird Deals',              count: rs.filter((r) => hotelTags(r).early_bird).length },
      { id: 'star_5',             label: '5 Star',                        count: rs.filter((r) => Math.floor(r.rating || 0) === 5).length },
      { id: 'star_4',             label: '4 Star',                        count: rs.filter((r) => Math.floor(r.rating || 0) === 4).length },
      { id: 'breakfast',          label: 'Breakfast Included',            count: rs.filter((r) => hotelTags(r).amenities.includes('Breakfast Included')).length },
      { id: 'unmarried_allowed',  label: 'Unmarried Couples Allowed',     count: rs.filter((r) => hotelTags(r).unmarried_allowed).length },
      { id: 'couple_recommended', label: 'Highly Recommended by Couples', count: rs.filter((r) => hotelTags(r).couple_recommended).length },
    ],
    apply: (h, sel) => {
      if (noSel(sel)) return true;
      return sel.some((id) => {
        if (id === 'star_5') return Math.floor(h.rating || 0) === 5;
        if (id === 'star_4') return Math.floor(h.rating || 0) === 4;
        if (id === 'breakfast') return hotelTags(h).amenities.includes('Breakfast Included');
        return hotelTags(h)[id];
      });
    },
  },
  {
    name: 'preference', label: 'Preference', type: 'segmented',
    options: () => [
      { id: 'per_night', label: 'Price Per Night' },
      { id: 'total',     label: 'Total Price' },
    ],
    apply: () => true, // affects display only, handled by parent
  },
  {
    name: 'price_bucket', label: 'Price Per Night', type: 'price-buckets',
    options: (rs) => PRICE_BUCKETS.map((b) => ({ ...b, count: rs.filter((r) => r.display_price >= b.min && r.display_price < b.max).length })),
    apply: (h, sel) => noSel(sel) || sel.some((id) => {
      const b = PRICE_BUCKETS.find((x) => x.id === id);
      return b && h.display_price >= b.min && h.display_price < b.max;
    }),
    // Inline custom-range under the same filter section (rendered after the buckets).
    customRange: {
      name: 'custom_price',
      apply: (h, val) => {
        if (!val || (val.min == null && val.max == null) || (val.min === '' && val.max === '')) return true;
        const p = Number(h.display_price);
        if (val.min !== '' && val.min != null && p < Number(val.min)) return false;
        if (val.max !== '' && val.max != null && p > Number(val.max)) return false;
        return true;
      },
    },
  },
  {
    name: 'star', label: 'Star Category', type: 'check-list',
    options: (rs) => [3, 4, 5].map((n) => ({ id: String(n), label: `${n} Star`, count: rs.filter((r) => Math.floor(r.rating || 0) === n).length })),
    apply: (h, sel) => noSel(sel) || sel.includes(String(Math.floor(h.rating || 0))),
  },
  {
    name: 'user_rating', label: 'User Rating', type: 'check-list',
    options: (rs) => [
      { id: '4.2', label: 'Excellent 4.2+', count: rs.filter((r) => (r.rating || 0) >= 4.2).length },
      { id: '3.5', label: 'Very Good 3.5+', count: rs.filter((r) => (r.rating || 0) >= 3.5).length },
      { id: '3.0', label: 'Good 3.0+',      count: rs.filter((r) => (r.rating || 0) >= 3.0).length },
    ],
    apply: (h, sel) => noSel(sel) || sel.some((id) => (h.rating || 0) >= Number(id)),
  },
  {
    name: 'property_type', label: 'Property Type', type: 'check-list',
    options: (rs) => PROPERTY_TYPES.map((t) => ({ id: t, label: t, count: rs.filter((r) => hotelTags(r).property_type === t).length })),
    apply: (h, sel) => noSel(sel) || sel.includes(hotelTags(h).property_type),
  },
  {
    name: 'locality', label: 'Top Locations', type: 'show-more-list',
    options: (rs) => {
      const counts = {};
      rs.forEach((r) => { const l = hotelTags(r).locality; if (l) counts[l] = (counts[l] || 0) + 1; });
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([id, count]) => ({ id, label: id, count }));
    },
    apply: (h, sel) => noSel(sel) || sel.includes(hotelTags(h).locality),
  },
  {
    name: 'room_view', label: 'Room Views', type: 'check-list',
    options: (rs) => [
      { id: 'garden', label: 'Garden View', count: rs.filter((r) => hotelTags(r).garden_view).length },
      { id: 'city',   label: 'City View',   count: rs.filter((r) => hotelTags(r).city_view).length },
    ],
    apply: (h, sel) => noSel(sel) || sel.some((id) => hotelTags(h)[`${id}_view`]),
  },
  {
    name: 'chain', label: 'Hotel Chains', type: 'show-more-list',
    options: (rs) => {
      const counts = {};
      rs.forEach((r) => { const c = hotelTags(r).chain; if (c) counts[c] = (counts[c] || 0) + 1; });
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([id, count]) => ({ id, label: id, count }));
    },
    apply: (h, sel) => noSel(sel) || sel.includes(hotelTags(h).chain),
  },
  {
    name: 'luxe', label: 'Luxe Collection', type: 'toggle',
    options: (rs) => ({ count: rs.filter((r) => hotelTags(r).luxe).length }),
    apply: (h, on) => !on || hotelTags(h).luxe,
  },
  {
    name: 'amenities', label: 'Amenities', type: 'amenity-search',
    options: (rs) => {
      const counts = {};
      rs.forEach((r) => hotelTags(r).amenities.forEach((a) => { counts[a] = (counts[a] || 0) + 1; }));
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([id, count]) => ({ id, label: id, count }));
    },
    apply: (h, sel) => noSel(sel) || sel.every((a) => hotelTags(h).amenities.includes(a)),
  },
  {
    name: 'booking_pref', label: 'Booking Preference', type: 'check-list',
    options: (rs) => [
      { id: 'caretaker',       label: 'Caretaker',       count: rs.filter((r) => hotelTags(r).caretaker).length },
      { id: 'instant_book',    label: 'Instant Book',    count: rs.filter((r) => hotelTags(r).instant_book).length },
      { id: 'entire_property', label: 'Entire Property', count: rs.filter((r) => hotelTags(r).entire_property).length },
    ],
    apply: (h, sel) => noSel(sel) || sel.every((id) => hotelTags(h)[id]),
  },
  {
    name: 'house_rules', label: 'House Rules', type: 'check-list',
    options: (rs) => [
      { id: 'pets_allowed',      label: 'Pets Allowed',              count: rs.filter((r) => hotelTags(r).pets_allowed).length },
      { id: 'self_checkin',      label: 'Self Check-In',             count: rs.filter((r) => hotelTags(r).self_checkin).length },
      { id: 'smoking_allowed',   label: 'Smoking Allowed',           count: rs.filter((r) => hotelTags(r).smoking_allowed).length },
      { id: 'unmarried_allowed', label: 'Unmarried Couples Allowed', count: rs.filter((r) => hotelTags(r).unmarried_allowed).length },
      { id: 'alcohol_allowed',   label: 'Alcohol Allowed',           count: rs.filter((r) => hotelTags(r).alcohol_allowed).length },
    ],
    apply: (h, sel) => noSel(sel) || sel.every((id) => hotelTags(h)[id]),
  },
  {
    name: 'deals', label: 'Deals & Offers', type: 'check-list',
    options: (rs) => [
      { id: 'cashback',        label: 'Cashback',        count: rs.filter((r) => hotelTags(r).cashback).length },
      { id: 'coupons',         label: 'Coupons',         count: rs.filter((r) => hotelTags(r).coupons).length },
      { id: 'seasonal_offers', label: 'Seasonal Offers', count: rs.filter((r) => hotelTags(r).seasonal_offers).length },
      { id: 'flash_sales',     label: 'Flash Sales',     count: rs.filter((r) => hotelTags(r).flash_sales).length },
    ],
    apply: (h, sel) => noSel(sel) || sel.some((id) => hotelTags(h)[id]),
  },
  {
    name: 'flex', label: 'Flexible Check-In / Check-Out', type: 'check-list',
    options: (rs) => [
      { id: 'early_checkin', label: 'Early Check-In', count: rs.filter((r) => hotelTags(r).early_checkin).length },
      { id: 'late_checkout', label: 'Late Check-Out', count: rs.filter((r) => hotelTags(r).late_checkout).length },
    ],
    apply: (h, sel) => noSel(sel) || sel.every((id) => hotelTags(h)[id]),
  },
];

// Sorting key based on "preference" segmented value.
export const getHotelSortKey = (pref) => pref === 'total' ? 'total_price_first' : 'price_asc';

export { uniq, hotelTags };
